import React, { useState } from 'react';

import {
  Flex,
  Button,
  InfoSection,
  Panel,
  Checkbox,
  TextInput,
  Label,
  OneQuarterLayout,
  TabButton,
  Divider,
  Tooltip
} from '@ubisend/pulse-components';
import { AnimateSharedLayout } from '@ubisend/framer-motion';
import { ImageSelect } from '@ubisend/pulse-files';
import { useLanguages, LanguagesConsumer } from '@ubisend/pulse-hooks';

import { useBotSettings } from '../../hooks/index';

const FullPageGeneralSettings = () => {
  const [selectImage, setSelectImage] = useState();

  const {
    settings,
    setSettings,
    language,
    setLanguage,
    name,
    focusBot,
    handleNewImage
  } = useBotSettings();
  const { languages } = useLanguages();

  const showSelectImage = () => setSelectImage(true);

  const hideSelectImage = () => setSelectImage(false);

  const handleShowNameToggle = () => {
    setSettings(settings => ({
      ...settings,
      name_enabled: !settings.name_enabled
    }));
  };

  const handleNameChange = event => {
    const newName = event.target.value;

    setSettings(settings => ({
      ...settings,
      names: settings.names.map(name => {
        if (name.language_id === language.id) {
          return {
            language_id: language.id,
            content: { name: newName }
          };
        }

        return name;
      })
    }));
  };

  const handleShowLogoToggle = () => {
    focusBot();
    setSettings(settings => ({
      ...settings,
      logo_enabled: !settings.logo_enabled
    }));
  };

  const handleImageChange = file => {
    handleNewImage(file);
    hideSelectImage();
  };

  const handleLogoLinkChange = event => {
    const value = event.target.value;

    setSettings(settings => ({ ...settings, logo_link: value }));
  };

  const handleResetBotOnClose = () => {
    setSettings(settings => ({
      ...settings,
      reset_on_close: !settings.reset_on_close
    }));
  };

  return (
    <Flex>
      <OneQuarterLayout>
        <InfoSection title="Main Settings" info="Add a logo to your bot." />
      </OneQuarterLayout>
      <Panel>
        {selectImage && (
          <ImageSelect
            handleCancel={hideSelectImage}
            handleFileSelect={handleImageChange}
          />
        )}
        <LanguagesConsumer>
          <Flex>
            <AnimateSharedLayout>
              {languages.map(languageTab => (
                <TabButton
                  onClick={() => setLanguage(languageTab)}
                  active={languageTab.id === language.id}
                  key={languageTab.id}>
                  {languageTab.name}
                </TabButton>
              ))}
            </AnimateSharedLayout>
          </Flex>
          <Divider fullWidth />
        </LanguagesConsumer>
        <Flex col ySpace>
          <Flex col ySpaceSm grow>
            <Checkbox
              checked={settings.name_enabled}
              onChange={handleShowNameToggle}
              label="Show chatbot name"
            />
            <TextInput
              disabled={!settings.name_enabled}
              aria-label="bot-name"
              value={name}
              onChange={handleNameChange}
              onClick={focusBot}
              maxLength="255"
            />
          </Flex>
          <Flex col ySpaceSm>
            <Checkbox
              checked={settings.logo_enabled}
              onChange={handleShowLogoToggle}
              label="Show chatbot logo"
            />
            <Button
              selfTop
              disabled={!settings.logo_enabled}
              variant="secondary"
              onClick={showSelectImage}>
              Change Logo
            </Button>
            <Label htmlFor="logo_link">Logo link</Label>
            <TextInput
              disabled={!settings.logo_enabled}
              id="logo_link"
              aria-label="logo link input"
              placeholder="https://website.com"
              value={settings.logo_link}
              onChange={handleLogoLinkChange}
            />
          </Flex>
          <Divider mtNone />
          <Flex center>
            <Checkbox
              checked={settings.reset_on_close}
              onChange={handleResetBotOnClose}
              label="Reset chatbot on close"
            />
            <Tooltip
              tooltip={
                <Tooltip.Content>
                  Reset the chatbot conversation back to the start when the
                  widget is closed
                </Tooltip.Content>
              }>
              <Tooltip.Icon>?</Tooltip.Icon>
            </Tooltip>
          </Flex>
        </Flex>
      </Panel>
    </Flex>
  );
};

export default FullPageGeneralSettings;
